import { useTranslation } from 'next-i18next'

import ResponsiveImage from '~/components/Image/ResponsiveImage'
import SVGImage from '~/components/Image/SVGImage'

import { useAppSelector } from '~/redux/hooks'

import SVGIcon from '~/components/Icons/svgs/SVGIcon'
import HexPlayButton from '~/components/Icons/svgs/HexPlayButton'
import HexActivityButton from '~/components/Icons/svgs/HexActivityButton'
import HexEncyclopediaIcon from '~/components/Icons/svgs/HexEncyclopediaIcon'

import { LearningPathColors } from '~/dashboard/types/dashboard.types'

import HexWhyButton from '~/components/Icons/svgs/HexWhyButton'

import { selectVccSubBadgeImage, selectVccSubBadgeOutlineImage } from '../slices/vcc-data.slice'
import { LevelActionType } from '../types/bootcamp.types'

interface IconDimension {
  height: number
  width: number
}

type IconSize = 'x-small' | 'small' | 'large'

export const VIDEO_COURSE_ICON: Record<
  LevelActionType,
  {
    i18nKey: 'icons.activitySetIcon' | 'icons.playButton'
    srcKey: string
    size: Partial<Record<IconSize, IconDimension>>
  }
> = {
  [LevelActionType.Why]: {
    i18nKey: 'icons.playButton',
    srcKey: 'bootcamp/video_icon.svg',
    size: {
      small: { height: 32, width: 32 },
      large: { height: 34, width: 34 },
    },
  },
  [LevelActionType.How]: {
    i18nKey: 'icons.playButton',
    srcKey: 'bootcamp/video_icon.svg',
    size: {
      small: { height: 32, width: 32 },
      large: { height: 34, width: 34 },
    },
  },
  [LevelActionType.Definition]: {
    i18nKey: 'icons.playButton',
    srcKey: 'bootcamp/video_icon.svg',
    size: {
      small: { height: 32, width: 32 },
      large: { height: 34, width: 34 },
    },
  },
  [LevelActionType.Activity]: {
    i18nKey: 'icons.activitySetIcon',
    srcKey: 'bootcamp/activity_set.svg',
    size: {
      small: { height: 32, width: 32 },
      large: { height: 34, width: 34 },
    },
  },
  // To fix ts warning
  [LevelActionType.Round]: {
    i18nKey: 'icons.activitySetIcon',
    srcKey: 'bootcamp/activity_set.svg',
    size: {
      small: { height: 32, width: 32 },
      large: { height: 34, width: 34 },
    },
  },
  [LevelActionType.Quiz]: {
    i18nKey: 'icons.activitySetIcon',
    srcKey: 'bootcamp/badge-shield.svg',
    size: {
      small: { height: 31, width: 27 },
      large: { height: 42, width: 36 },
    },
  },
}

interface IconProp {
  iconType: LevelActionType
  size?: IconSize
  className?: string
  color: LearningPathColors
}
const mediaQuerySmall = [{ screen: '(min-width: 280px)', width: '39px' }]
const mediaQueryLarge = [{ screen: '(min-width: 280px)', width: '51px' }]

const VideoCourseIcon = ({ iconType, size = 'small', className, color }: IconProp): React.ReactElement => {
  const getIconComponent = (
    componentType: LevelActionType,
  ): ((props: { [key: string]: any }) => React.ReactElement) => {
    switch (componentType) {
      case LevelActionType.Activity:
        return HexActivityButton
      case LevelActionType.How:
      case LevelActionType.Definition:
      case LevelActionType.Quiz:
        return HexPlayButton
      case LevelActionType.Why:
        return HexWhyButton
      case LevelActionType.Round:
        return HexActivityButton
      case LevelActionType.Encyclopedia:
        return HexEncyclopediaIcon
      default:
        return HexPlayButton
    }
  }

  const icon = getIconComponent(iconType)
  const isLarge = size === 'large'
  return (
    <div className={`${className} ${isLarge && '-left-13 -top-11'}`}>
      <SVGIcon
        className="relative hover:scale-110 duration-300"
        colorMatrix={color.colorMatrix}
        gradientStart={color.start}
        gradientStop={color.stop}
        icon={icon}
        size={size === 'x-small' ? 39 : isLarge ? 88 : 42}
      />
    </div>
  )
}

export const QuizIcon = ({
  isCompleted,
  size = 'small',
  className = '',
}: {
  isCompleted: boolean
  size?: IconSize
  className?: string
}): React.ReactElement => {
  const subBadge = useAppSelector(selectVccSubBadgeImage)
  const outlineSubBadge = useAppSelector(selectVccSubBadgeOutlineImage)

  const { t } = useTranslation('imageAlt')
  const icon = VIDEO_COURSE_ICON[LevelActionType.Quiz]
  const iconDimension = icon.size[size]
  const badgeImage = isCompleted ? subBadge : outlineSubBadge

  return (
    <div className={`-ml-1 z-0 inline-block relative ${className}`} style={{ width: size === 'small' ? 35 : 51 }}>
      <SVGImage
        alt={t('badgeShield')}
        height={iconDimension.height}
        imageClassName="mx-auto"
        srcKey={icon.srcKey}
        width={iconDimension.width}
      />
      {size === 'small' ? (
        <ResponsiveImage
          alt={badgeImage.srcKey}
          className="absolute -top-1 left-0 z-10"
          fit="contain"
          height={33}
          imageWidths="39"
          mediaQuery={mediaQuerySmall}
          srcKey={badgeImage.srcKey}
          width={39}
        />
      ) : (
        <ResponsiveImage
          alt={badgeImage.srcKey}
          className="absolute -top-1 left-0 z-10"
          fit="contain"
          height={43}
          imageWidths="51"
          mediaQuery={mediaQueryLarge}
          srcKey={badgeImage.srcKey}
          width={51}
        />
      )}
    </div>
  )
}

export default VideoCourseIcon
